import { DOCUMENT } from '@angular/common';
import type { Renderer2 } from '@angular/core';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { CheckoutUrlBuilderService } from '@core-mkt/shared/services/checkout-url-builder/checkout-url-builder.service';
import { AccessibilityService } from '../accessibility/accessibility.service';
import { AceModeHighlightService } from '../ace-mode-highlight/ace-mode-highlight.service';
import { AceModePillService } from '../ace-mode-pill/ace-mode-pill.service';
import { BrandConfigurationService } from '../configuration/brand-configuration.service';
import { CurrentYearService } from '../current-year/current-year.service';
import { EnvService } from '../env/env.service';
import { GeolocationService } from '../geolocation/geolocation.service';
import { PlayAudioService } from '../play-audio/play-audio.service';
import { ProductPickerService } from '../product-picker/product-picker.service';
import type { QueryParam } from '../query-param/query-helpers';
import {
  addQueryParamstoAnchor,
  getQueryParamString,
  isQueryParamFullLink,
  isRelativeLink,
} from '../query-param/query-helpers';
import { QueryParamService } from '../query-param/query-param.service';
import { TrustpilotStarsService } from '../trustpilot-stars/trustpilot-stars.service';
import { WistiaService } from '../wistia/wistia.service';

// youtube-player ships no types; this is the slice of its default export we call.
interface YouTubePlayerModule {
  default(el: Element, options: { videoId: string }): { playVideo(): void };
}

@Injectable({
  providedIn: 'root',
})
export class WysiwygRedactorService {
  player: YouTubePlayerModule;
  queryParams: QueryParam = {};
  renderer: Renderer2;
  geolocationSettings = {
    state: undefined as string,
    loadingPlaceholder: 'Loading state ' as string,
    token: /\${state}/g,
    selectors: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'a', 'span', 'div', 'li', 'td', 'th', 'label'] as string[],
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private brand: BrandConfigurationService,
    private wistia: WistiaService,
    private geolocation: GeolocationService,
    private productPicker: ProductPickerService,
    private trustpilotStars: TrustpilotStarsService,
    private aceModeHighlight: AceModeHighlightService,
    private aceModePill: AceModePillService,
    private playAudio: PlayAudioService,
    private currentYear: CurrentYearService,
    private queryParamService: QueryParamService,
    private accessibilityService: AccessibilityService,
    private env: EnvService,
    private checkoutUrlBuilder: CheckoutUrlBuilderService,
  ) {
    if (this.route.snapshot) {
      this.queryParams = this.queryParamService.getQueryParams();
    }

    this.geolocation.stateAbbr.subscribe((state) => {
      this.geolocationSettings.state = this.geolocation.getStateName(state);

      if (state) {
        this.updateLocation();
      }
    });
  }

  /**
   * Updates the content of elements in the document based on geolocation settings.
   *
   * This method selects elements in the document using the selectors defined in
   * `geolocationSettings`. It then updates the inner HTML of these elements by
   * replacing placeholders with the current state or a loading placeholder.
   *
   * The method performs the following steps:
   * 1. Retrieves the selectors, loading placeholder, state, and token from `geolocationSettings`.
   * 2. Selects all elements in the document that match the selectors.
   * 3. Iterates over each selected element and updates its content:
   *    - If the element's text content includes the loading placeholder and the state is defined,
   *      it replaces the loading placeholder with the state.
   *    - If the element's text content matches the token, it replaces the token with the state
   *      if defined, otherwise with the loading placeholder.
   *
   * @returns {void}
   */
  public updateLocation(): void {
    const { selectors, loadingPlaceholder, state, token } = this.geolocationSettings;
    const nodes: Element[] = selectors.flatMap((selector) => Array.from(this.document.querySelectorAll(selector)));

    // Rewrite text nodes only: re-parsing innerHTML would turn the state string into markup and
    // re-run anything the CMS markup still carries.
    const replacement = state ?? loadingPlaceholder;
    nodes.forEach((el: Element) => {
      for (const node of this.textNodesOf(el)) {
        const before = node.nodeValue ?? '';
        let text = before;
        if (state && text.includes(loadingPlaceholder)) {
          text = text.replace(loadingPlaceholder, () => state);
        }
        text = text.replace(token, () => replacement);
        if (text !== before) {
          node.nodeValue = text;
        }
      }
    });
  }

  private textNodesOf(root: Node): Text[] {
    const nodes: Text[] = [];
    const walk = (parent: Node) =>
      parent.childNodes.forEach((child) => (child.nodeType === 3 ? nodes.push(child as Text) : walk(child)));
    walk(root);
    return nodes;
  }

  /**
   * YouTube iframes by default have a static width and height, which causes a horizontal scroll
   * to appear on mobile due to the static width being larger than the mobile width.
   *
   * This method adds a class to force the iframe to be responsive while keeping the standard
   * YouTube aspect ratio of 16:9.
   *
   * It's important to note that Redactor by default automatically wraps all iframes with a
   * <figure> element, which is great because that's where we must add our .aspect-ratio-16-9 class.
   */
  async makeYouTubeIframeResponsive(renderer: Renderer2): Promise<void> {
    this.renderer = renderer;
    const selector = 'iframe[src*="youtube.com"]';
    const youtubeIframes = Array.from(this.document.querySelectorAll(selector));

    if (youtubeIframes.length > 0) {
      // If we have youtube iframes, then dynamically load our youtube-player to help
      // us load and autoplay+sound youtube iframes if a user clicks a poster
      this.player = (await import('youtube-player')) as YouTubePlayerModule;

      youtubeIframes.forEach((el) => {
        const parentEl = this.renderer.parentNode(el) as HTMLElement;
        const grandparentEl = this.renderer.parentNode(parentEl) as HTMLElement;
        // Make responsive
        parentEl.classList.add('aspect-ratio-16-9');
        grandparentEl.parentElement.classList.add('my-auto');

        // Hide the iframe until clicked
        this.makeYouTubePoster(el);
      });
    }
  }

  /**
   * Removes the YouTube iframe, and replaces it with a poster. This improves performance by
   * reducing network calls on page load, and only loading them when the user clicks the poster
   * to load and play the video.
   */
  private makeYouTubePoster(el: Element): void {
    const embedId = this.youtubeIdParser(el.getAttribute('src'));
    if (embedId?.length) {
      const posterEl = this.createPosterEl(embedId);
      const elParent = this.renderer.parentNode(el) as Node;
      this.renderer.appendChild(elParent, posterEl);
      el.remove(); // Remove the original iframe so we don't load YouTube and its scripts
    }
  }

  /**
   * Creates the node element that will contain the poster and play icon.
   */
  private createPosterEl = (embedId: string): Element => {
    const absoluteCenter = 'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
    const posterClass = 'poster';
    const poster = this.renderer.createElement('div') as HTMLDivElement;
    poster.classList.add(posterClass, 'group');

    poster.innerHTML = `
      <img class="${posterClass}__img pointer-events-none" src="https://img.youtube.com/vi/${embedId}/hqdefault.jpg" loading="lazy" width="480px" height="360px" alt="YouTube video poster" />
      <div class="pointer-events-none ${absoluteCenter}">
        <svg class="fill-current text-ace-pink-600 group-hover:text-ace-pink-400 w-12" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
          <path d="M361 215C375.3 223.8 384 239.3 384 256C384 272.7 375.3 288.2 361 296.1L73.03 472.1C58.21 482 39.66 482.4 24.52 473.9C9.377 465.4 0 449.4 0 432V80C0 62.64 9.377 46.63 24.52 38.13C39.66 29.64 58.21 29.99 73.03 39.04L361 215z"/>
        </svg>
      </div>
    `;
    poster.onclick = (event) => {
      const target = event.target as Element;
      if (target.classList.contains(posterClass)) {
        const videoContainer = this.document.createElement('div');
        target?.parentElement.append(videoContainer);
        const embed = this.player.default(target, { videoId: embedId });
        embed.playVideo();
      }
    };

    return poster;
  };

  /**
   * Extract the YouTube ID from the embed URL. Regex source came from:
   * https://stackoverflow.com/questions/3452546/how-do-i-get-the-youtube-video-id-from-a-url
   */
  private youtubeIdParser(url: string): string {
    const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    // IDs are interpolated into poster HTML, so accept only the 11-char YouTube ID alphabet.
    if (match && /^[\w-]{11}$/.test(match[2])) {
      return match[2];
    }
    return;
  }

  private hasBypassAction(actionBypass: string): boolean {
    return (
      actionBypass !== undefined && (actionBypass === 'lazy' || actionBypass === 'nofollow' || actionBypass === 'lcp')
    );
  }

  /**
   * For above-the-fold WYSIWYG content: first image gets fetchpriority="high" (LCP candidate),
   * all subsequent images get loading="lazy".
   */
  private addFetchPriorityToFirstImage(content: string): string {
    if (!content || typeof content !== 'string') return content;
    let isFirst = true;

    return content.replace(/<img\b[^>]*>/gi, (match) => {
      let updatedTag = match;

      const hasLoading = /\sloading\s*=\s*["'][^"']*["']/i.test(updatedTag);
      const hasFetchPriority = /\sfetchpriority\s*=\s*["'][^"']*["']/i.test(updatedTag);

      if (isFirst) {
        isFirst = false;
        // Ensure first image has fetchpriority="high"
        if (hasFetchPriority) {
          updatedTag = updatedTag.replace(/(\sfetchpriority\s*=\s*["'])[^"]*(["'])/i, '$1high$2');
        } else {
          updatedTag = updatedTag.replace(/>$/, ' fetchpriority="high">');
        }
        return updatedTag;
      }

      // Subsequent images: ensure loading="lazy"
      if (hasLoading) {
        updatedTag = updatedTag.replace(/(\sloading\s*=\s*["'])[^"]*(["'])/i, '$1lazy$2');
      } else {
        updatedTag = updatedTag.replace(/>$/, ' loading="lazy">');
      }

      return updatedTag;
    });
  }

  /**
   * Returns the src of the first <img> in an HTML string, or null if none found.
   * The returned URL includes imgix formatting to match what bypassSanitizer will
   * produce during client-side hydration, ensuring preload URLs don't mismatch.
   */
  extractFirstImageSrc(content: string): string | null {
    if (!content || typeof content !== 'string') return null;
    const match = content.match(/<img[^>]+src="([^"]+)"/);
    const src = match?.[1] ?? null;
    // Inside a responsive <picture> the <img> is only the smallest-breakpoint fallback;
    // preloading it would fetch the wrong asset on wider viewports, so emit no preload.
    if (src && /<picture\b/i.test(content.slice(0, match.index))) return null;
    return src ? this.formatImgixUrl(src) : null;
  }

  /**
   * Applies imgix auto=format,compress params to a URL, matching the
   * transformation that addXgritImgFormatting applies to HTML content.
   */
  formatImgixUrl(url: string): string {
    if (!url || !url.includes('xgrit-ecom.imgix.net')) return url;
    if (!url.includes('?')) return url + '?auto=format,compress';
    if (url.includes('auto=format,compress')) return url;
    if (url.includes('auto=format')) return url + ',compress';
    return url + '&auto=format,compress';
  }

  /**
   * Parses only shortcode tokens (e.g. [productPicker]) into placeholder elements and returns
   * trusted HTML. Used for SSR pre-processing so ShortcodeRendererDirective can replace
   * placeholders during ngAfterViewInit without waiting for the async bypassSanitizer() call.
   */
  parseShortcodesOnly(content: string): string {
    if (typeof content !== 'string' || !content) return content;
    content = this.wistia.wistiaParser(content);
    content = this.productPicker.productPickerParser(content);
    content = this.trustpilotStars.trustpilotStarsParser(content);
    content = this.aceModeHighlight.aceModeHighlightParser(content);
    content = this.playAudio.playAudioParser(content);
    content = this.currentYear.currentYearParser(content);
    content = this.toEnvCheckoutLinks(content);
    // parseShortcodesOnly is the SERVER-render path for deferred content (LayoutComponent),
    // where the full bypassSanitizer() only runs later on the client. Without this, the SSR
    // HTML that crawlers see ships slash-less links and only the hydrated client gets them.
    // Slashing is a synchronous, SSR-safe string pass, so run it here too.
    if (this.env.get.enforceTrailingSlash) {
      content = this.addTrailingSlashToLinks(content);
    }
    return this.sanitizer.bypassSecurityTrustHtml(content) as string;
  }

  /* Angular strips iframes by default. this must be overridden to show content.
   */
  bypassSanitizer(content: string, actionBypass?: string): string {
    if (typeof content !== 'string') return content;
    if (content === null || content === undefined) return '';

    // CRITICAL: Parse shortcodes FIRST before HTML modifications
    // This prevents addQueryParams, addLazyLoading, etc. from corrupting shortcode attributes
    // which contain URLs or other HTML-like content. Shortcode parsers extract and replace
    // the shortcode syntax with placeholder elements before DOM manipulation occurs.
    content = this.wistia.wistiaParser(content);
    content = this.productPicker.productPickerParser(content);
    content = this.trustpilotStars.trustpilotStarsParser(content);
    content = this.aceModeHighlight.aceModeHighlightParser(content);
    content = this.aceModePill.aceModePillParser(content);
    content = this.playAudio.playAudioParser(content);
    content = this.currentYear.currentYearParser(content);

    // Now apply HTML modifications (these modify actual HTML tags, not shortcodes)
    content = this.toEnvCheckoutLinks(content);
    // Trailing slash MUST run before addQueryParams so query strings append after the
    // slash (e.g. /path/?utm=x). CKEditor/CMS hrefs are otherwise emitted verbatim.
    if (this.env.get.enforceTrailingSlash) {
      content = this.addTrailingSlashToLinks(content);
    }
    content = this.addQueryParams(content);
    content = this.addXgritImgFormatting(content);
    if (!actionBypass || !this.hasBypassAction(actionBypass)) {
      content = this.addLazyLoading(content);
      content = this.addNofollow(content);
    } else if (actionBypass === 'lcp') {
      content = this.addFetchPriorityToFirstImage(content);
    }

    // Find all anchor tags in the content
    const anchorTags = this.getAnchorTags(content);
    if (anchorTags.length > 0) {
      anchorTags.forEach((value) => {
        const hrefValue = this.getHrefValueFromLink(value);
        const ariaLabelContext = this.accessibilityService.getTextContextForLinkInContent(hrefValue);
        const newContent = this.addAriaLabel(
          content,
          'id-' + ariaLabelContext?.replace(/\s+/g, '-'),
          ariaLabelContext,
          hrefValue,
        );
        content = newContent;
      });
    }

    const bypassed = this.sanitizer.bypassSecurityTrustHtml(content) as string;

    return bypassed;
  }

  /**
   * Process content with initial sanitization, removing pricing placeholders for later replacement
   * Use this for initial content processing when product may not be available yet
   */
  processContentWithPlaceholderRemoval(content: string, actionBypass?: string): string {
    if (!content) return content;

    let processedContent = content;

    // Remove pricing placeholders for initial render - will be replaced when product available
    if (this.containsPricingPlaceholders(content)) {
      processedContent = this.removePricingPlaceholders(content);
    }

    return this.bypassSanitizer(processedContent, actionBypass);
  }

  /**
   * Check if content contains pricing placeholders
   * Safely handles null, undefined, and non-string values
   */
  containsPricingPlaceholders(content: string | null | undefined): boolean {
    if (!content || typeof content !== 'string') {
      return false;
    }

    return (
      content.includes('${this.crossedOutPrice}') ||
      content.includes('${this.finalPrice}') ||
      content.includes('${this.originalPrice}')
    );
  }

  /**
   * Remove pricing placeholders from content (fallback when no product available)
   */
  private removePricingPlaceholders(content: string): string {
    return content
      .replace(/\$\{this\.crossedOutPrice\}/g, '')
      .replace(/\$\{this\.finalPrice\}/g, '')
      .replace(/\$\{this\.originalPrice\}/g, '');
  }

  private getHrefValueFromLink(link: string): string {
    if (link && link.includes('href=')) {
      const hrefMatch = link.match(/href="([^"]*)"/);
      return hrefMatch ? hrefMatch[1] : '';
    }
    return '';
  }

  private getAnchorTags(content: string): string[] {
    const tagRegex = /<a\b[^>]*>(.*?)<\/a>/gi;
    const tags = content.match(tagRegex);
    return tags || [];
  }

  private addAriaLabel(html: string, spanId: string, spanContext: string, targetHref: string): string {
    return html.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (_match: string, attributes: string, content: string) => {
      const hrefMatch = attributes.match(/href=["']([^"']+)["']/i);
      if (!hrefMatch || hrefMatch[1] !== targetHref) {
        return `<a${attributes}>${content}</a>`;
      }
      return `<a${attributes} aria-labelledby="${spanId}">${content}<span id="${spanId}" class="sr-only">${spanContext}</span></a>`;
    });
  }

  /**
   * Applies `CheckoutUrlBuilderService.toEnvCheckoutHost` to every href in the content. A
   * scheme-relative href (`//host/path`) is parsed as https and handed back in its original form.
   */
  private toEnvCheckoutLinks(content: string): string {
    return this.mapHrefs(content, (url) =>
      url.startsWith('//')
        ? this.checkoutUrlBuilder.toEnvCheckoutHost(`https:${url}`).replace(/^https:/, '')
        : this.checkoutUrlBuilder.toEnvCheckoutHost(url),
    );
  }

  /**
   * Rewrites each `href="..."` / `href='...'` value with `rewrite`, keeping its quote style. The
   * leading-whitespace guard avoids matching `data-href`.
   */
  private mapHrefs(content: string, rewrite: (url: string) => string): string {
    return content.replace(
      /(\s)href=(["'])([^"']*)\2/gi,
      (_match: string, precedingWhitespace: string, quote: string, url: string) =>
        `${precedingWhitespace}href=${quote}${rewrite(url)}${quote}`,
    );
  }

  /**
   * Adds a trailing slash to INTERNAL link hrefs that are missing one so CMS/CKEditor rich-text
   * links are canonical (avoids the SSR 301 redirect hop crawlers otherwise incur). "Internal"
   * means a relative path OR an absolute URL pointing at one of our own sites (brand host +
   * sibling sites). EXTERNAL absolute URLs are left untouched — their redirects can break on a
   * trailing slash (EPS-4640 field report).
   *
   * The slash is placed on the PATH, before any `?query` or `#hash`, so tracking params and
   * anchors are never corrupted (e.g. `/foo/?utm=x`, `/page/#faq`). Handles both quote styles
   * (`href="..."` / `href='...'`); the leading-whitespace guard avoids matching `data-href`.
   */
  addTrailingSlashToLinks(content: string): string {
    if (!content || typeof content !== 'string') return content;
    const hostName = this.brand.hostName;
    const siblingSites = this.brand.siblingSites;
    return this.mapHrefs(content, (url) => this.addTrailingSlashToHref(url, hostName, siblingSites));
  }

  /**
   * Normalizes a single href to carry a trailing slash on its path. Returns the value
   * unchanged when a slash would be invalid, would break the link, or targets an external site:
   *   - non-http(s) schemes (`mailto:`, `tel:`, `javascript:`, `data:`, …)
   *   - EXTERNAL absolute URLs — any absolute URL whose host is not our brand host or a sibling
   *     site. External redirects can break on a trailing slash (EPS-4640 field report). Absolute
   *     URLs pointing at our OWN sites are normalized.
   *   - file/asset URLs whose last path segment has an extension (`.pdf`, `.jpg`, `.html`, …)
   *   - paths already ending in `/`, and pure-anchor hrefs (`#faq`, empty path)
   */
  private addTrailingSlashToHref(url: string, hostName: string, siblingSites: string[]): string {
    if (!url) return url;
    // Non-http(s) schemes (mailto:, tel:, javascript:, data:, …) — a slash breaks them.
    const scheme = url.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
    if (scheme && !/^https?$/i.test(scheme[1])) return url;
    // Absolute URLs (scheme:// or protocol-relative //) are only normalized when they point at
    // one of OUR sites; external hosts are left untouched.
    const isAbsolute = !!scheme || url.startsWith('//');
    if (isAbsolute && !isQueryParamFullLink(url, hostName, siblingSites)) return url;
    // Split off any ?query / #hash so the slash lands on the path, not inside a param.
    const queryIdx = url.indexOf('?');
    const hashIdx = url.indexOf('#');
    const sepIdx = Math.min(queryIdx === -1 ? Infinity : queryIdx, hashIdx === -1 ? Infinity : hashIdx);
    const base = sepIdx === Infinity ? url : url.slice(0, sepIdx);
    const suffix = sepIdx === Infinity ? '' : url.slice(sepIdx);
    // Nothing to do for pure-anchor hrefs (empty base) or paths already ending in a slash.
    if (!base || base.endsWith('/')) return url;
    // For our absolute URLs, isolate the path after scheme+host so the file-extension check
    // inspects only the PATH (else a host like "aceable.com" reads ".com" as an extension), and
    // a bare host (https://aceable.com) still gets its slash.
    const authority = base.match(/^(?:[a-zA-Z][a-zA-Z0-9+.-]*:)?\/\//);
    if (authority && base.indexOf('/', authority[0].length) === -1) return `${base}/${suffix}`;
    // Leave file/asset URLs alone — a trailing slash would 404 the download (e.g. /doc.pdf/).
    const lastSegment = base.slice(base.lastIndexOf('/') + 1);
    if (/\.[a-z0-9]+$/i.test(lastSegment)) return url;
    return `${base}/${suffix}`;
  }

  addLazyLoading(content: string): string {
    const imgRegex = /<img[^<]*\/>/g;
    if (content && typeof content === 'string') {
      return content.replace(imgRegex, (match) => {
        match = match.substring(0, match.length - 2);
        match += 'loading="lazy" />';
        return match;
      });
    }
    return content;
  }

  addXgritImgFormatting(content: string): string {
    // `srcset` covers the <source> entries of a CKEditor responsive <picture>.
    const srcRegex = /(?:src|srcset)="(https:\/\/xgrit-ecom.imgix.net[^"]*)"/g;
    if (content && typeof content === 'string') {
      return content.replace(srcRegex, (srcMatch) => {
        let srcStrBeforeEnd = srcMatch.substring(0, srcMatch.length - 1);
        if (!srcStrBeforeEnd.includes('?')) {
          srcStrBeforeEnd += '?auto=format,compress"';
        } else if (srcMatch.includes('auto=format"')) {
          srcStrBeforeEnd += ',compress"';
        } else {
          srcStrBeforeEnd += '&auto=format,compress"';
        }
        return srcStrBeforeEnd;
      });
    }
    return content;
  }

  addNofollow(content: string): string {
    const linkRegex = /<a[^>]*[ >]/g;
    if (content.includes('nofollow-link')) {
      return content.toString().replace(linkRegex, (match) => {
        const relIndex = match.indexOf('rel=') + 5;
        if (relIndex < 2) {
          return match.substring(0, match.length - 2) + ' rel="nofollow" ';
        } else {
          return match.substring(0, relIndex) + 'nofollow ' + match.substring(relIndex, match.length);
        }
      });
    }
    return content;
  }

  addQueryParams(content: string): string {
    const linkRegex = /href="[^ >]*"[ >]/g;
    const queryString = getQueryParamString(this.queryParams);
    const hostName = this.brand.hostName;
    const siblingSites = this.brand.siblingSites;
    if (content && queryString.length > 1) {
      return content.toString().replace(linkRegex, (match) => {
        const hasEndTag = match.includes('>');
        if (!isQueryParamFullLink(match, hostName, siblingSites) && !isRelativeLink(match)) {
          return match;
        } else if (match.includes('#')) {
          // addQueryParamstoAnchor takes a bare URL; keep href=" and the closing quote/terminator outside it.
          const url = match.slice('href="'.length, -2);
          return `href="${addQueryParamstoAnchor(queryString, url)}"${match.slice(-1)}`;
        } else {
          match = match.substring(0, match.length - 2);
          const endString = hasEndTag ? '>' : ' ';
          if (match.includes('?')) {
            const newMatchString = match + '&' + queryString + '"' + endString;
            return newMatchString;
          }
          const newMatchString = match + '?' + queryString + '"' + endString;
          return newMatchString;
        }
      });
    }
    return content;
  }
}
